import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Section from '../components/common/Section';
import Button from '../components/common/Button';
import { FaArrowLeft, FaCheckCircle } from 'react-icons/fa';
import servicesData from '../content/data/services.json';

const ServiceDetailContainer = styled.div``;

const HeroSection = styled.div`
  padding: 6rem 0 4rem;
  background-color: var(--primary-color);
  color: white;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  max-width: 800px;
  margin: 0 auto;
  color: rgba(255, 255, 255, 0.9);
  
  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 3rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div``;

const ServiceDescription = styled.div`
  margin-bottom: 3rem;
  
  p {
    font-size: 1.125rem;
    line-height: 1.8;
    color: var(--text-light);
    margin-bottom: 1.5rem;
  }
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 2rem 0 3rem;
`;

const FeatureItem = styled.li`
  padding: 1.25rem;
  background-color: white;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  margin-bottom: 1rem;
  display: flex;
  align-items: flex-start;
  
  svg {
    color: var(--primary-color);
    margin-right: 1rem;
    margin-top: 0.25rem;
    flex-shrink: 0;
  }
  
  div {
    flex: 1;
  }
  
  h4 {
    margin-bottom: 0.5rem;
  }
  
  p {
    color: var(--text-light);
    margin: 0;
  }
`;

const Sidebar = styled.div`
  @media (max-width: 992px) {
    order: -1;
  }
`;

const SidebarCard = styled.div`
  background-color: white;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  padding: 2rem;
  margin-bottom: 2rem;
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -0.5rem;
    left: 0;
    width: 40px;
    height: 3px;
    background-color: var(--primary-color);
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  margin-bottom: 2rem;
  color: var(--primary-color);
  font-weight: 500;
  
  svg {
    margin-right: 0.5rem;
  }
  
  &:hover {
    text-decoration: underline;
  }
`;

const ServiceDetail = () => {
  const { id } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    // Find the service with the matching ID
    if (servicesData && servicesData.services) {
      const foundService = servicesData.services.find(s => s.id === id);
      
      if (foundService) {
        setService(foundService);
      }
      
      setLoading(false);
    }
  }, [id]);
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (!service) {
    return (
      <Section>
        <div style={{ textAlign: 'center' }}>
          <h2>Service Not Found</h2>
          <p>The service you're looking for does not exist.</p>
          <Button to="/services">Back to Services</Button>
        </div>
      </Section>
    );
  }
  
  // Define additional features for demonstration
  const serviceFeatures = [
    {
      title: "Real-time Monitoring",
      description: "Monitor your positions and exposures in real-time with advanced dashboards and alerts."
    },
    {
      title: "Seamless Integration",
      description: "Easily integrate with your existing systems and data sources for a unified view."
    },
    {
      title: "Customizable Reporting",
      description: "Generate custom reports tailored to your specific business needs and regulatory requirements."
    },
    {
      title: "Advanced Analytics",
      description: "Leverage sophisticated analytics to gain insights and make informed trading decisions."
    }
  ];
  
  return (
    <ServiceDetailContainer>
      <HeroSection>
        <div className="container">
          <HeroTitle>{service.title}</HeroTitle>
          <HeroSubtitle>
            {service.shortDescription}
          </HeroSubtitle>
        </div>
      </HeroSection>
      
      <Section>
        <BackLink to="/services"><FaArrowLeft /> Back to Services</BackLink>
        
        <ContentWrapper>
          <MainContent>
            <ServiceDescription>
              <p>{service.fullDescription}</p>
              <p>Our {service.title.toLowerCase()} solution is designed specifically for commodity trading firms, with a focus on power and gas markets. We understand the unique challenges faced by traders and risk managers in these markets and have built our tools to address these specific needs.</p>
            </ServiceDescription>
            
            <h3>Key Features</h3>
            <FeaturesList>
              {service.features.map((feature, index) => (
                <FeatureItem key={index}>
                  <FaCheckCircle size={24} />
                  <div>
                    <h4>{feature}</h4>
                    <p>Our {service.title.toLowerCase()} solution provides powerful tools to help you optimize your trading operations and stay ahead in rapidly evolving markets.</p>
                  </div>
                </FeatureItem>
              ))}
            </FeaturesList>
            
            <h3>Additional Features</h3>
            <FeaturesList>
              {serviceFeatures.map((feature, index) => (
                <FeatureItem key={index}>
                  <FaCheckCircle size={24} />
                  <div>
                    <h4>{feature.title}</h4>
                    <p>{feature.description}</p>
                  </div>
                </FeatureItem>
              ))}
            </FeaturesList>
          </MainContent>
          
          <Sidebar>
            <SidebarCard>
              <CardTitle>Get Started</CardTitle>
              <p>Ready to enhance your trading operations with our {service.title} solution?</p>
              <Button to="/contact" fullWidth variant="primary">Request a Demo</Button>
            </SidebarCard>
            
            <SidebarCard>
              <CardTitle>Related Services</CardTitle>
              <ul style={{ padding: 0, listStyle: 'none' }}>
                {servicesData.services
                  .filter(s => s.id !== id)
                  .slice(0, 3)
                  .map((relatedService, index) => (
                    <li key={index} style={{ marginBottom: '1rem' }}>
                      <Link to={`/services/${relatedService.id}`} style={{ color: 'var(--primary-color)', fontWeight: '500' }}>
                        {relatedService.title}
                      </Link>
                    </li>
                  ))
                }
              </ul>
            </SidebarCard>
          </Sidebar>
        </ContentWrapper>
      </Section>
    </ServiceDetailContainer>
  );
};

export default ServiceDetail; 